import * as React from "react"
import {
  TabbedForm,
  SaveButton,
  DeleteWithConfirmButton,
  Toolbar,
  useRecordContext
} from "react-admin"

import { Box } from "@mui/material";

import ImagesTab from "../products/ImagesTab"
import BasicInfoTab from "./BasicInfoTab"
import ColorOptionsTab from "./ColorOptionsTab"
import EditableAreasTab from "./EditableAreasTab"
import QuantityDiscountsTab from "../products/QuantityDiscountsTab"
import DescriptionTab from "../products/DescriptionTab"

const defaultValues = {
  productType: "clothes",
  sorting: 1,
  code: "",
  name: "",
  categories: [],
  price: "",
  stock: 0,
  minOrderQuantity: 1,
  maxOrderQuantity: "",
  samplingTime: "",
  samplingFee: "",
  minQuantity: 1,
  maxQuantity: 10,
  incrementStep: 1,
  description: "",
  specification: "",
  status: 1,
  coverImage: [],
  templateImage: [],
  sizeReferenceImage: [],
  printMethods: [
    {
      printinMethodId: ""
    }
  ],
  colors: [
    {
      name: "",
      code: "",
    }
  ],
  quantityDiscounts: [
    {
      minQuantity: "",
      maxQuantity: "",
      discountPercentage: "",
      leadTimeDays: "",
      discountPrice: "",
    }
  ],
  editableAreas: [
    {
      areaName: "",
      templateImage: [],
      image: []
    }
  ],
  isLogoColor: false,
  logoColor: "#000000",
  logoOpacity: 1,
}

const validateForm = (values) => {
  const errors = {};

  if (!values.name) {
    errors.name = "ra.validation.required";
  }

  // 如果上架狀態為 OFF (status 為 false 或 0)，則只檢查名稱欄位
  if (values.status === false || values.status === 0) {
    return errors; // 只檢查名稱，其他欄位不檢查
  }

  if (!values.coverImage?.rawFile && !values.coverImage?.[0]?.title) {
    errors.coverImage = "ra.validation.required";
  }

  if (!values.templateImage?.rawFile && !values.templateImage?.[0]?.title) {
    errors.templateImage = "ra.validation.required";
  }

  if (!values.sizeReferenceImage?.rawFile && !values.sizeReferenceImage?.[0]?.title) {
    errors.sizeReferenceImage = "ra.validation.required";
  }

  if (!Array.isArray(values.categories) || values.categories.length === 0) {
    errors.categories = "ra.validation.required";
  }

  if (!values.price) {
    errors.price = "ra.validation.required";
  }

  if (!values.bulkPrice) {
    errors.bulkPrice = "ra.validation.required";
  }

  if (!values.samplingTime) {
    errors.samplingTime = "ra.validation.required";
  }

  if (!values.samplingFee) {
    errors.samplingFee = "ra.validation.required";
  }

  if (!values.blankSampleLeadTimeDays) {
    errors.blankSampleLeadTimeDays = "ra.validation.required";
  }

  if (!values.minQuantity) {
    errors.minQuantity = "ra.validation.required";
  }

  if (!values.maxQuantity) {
    errors.maxQuantity = "ra.validation.required";
  }

  if (!values.incrementStep) {
    errors.incrementStep = "ra.validation.required";
  }

  if (!values.samplingMinOrderQuantity) {
    errors.samplingMinOrderQuantity = "ra.validation.required";
  }

  if (!values.samplingMaxOrderQuantity) {
    errors.samplingMaxOrderQuantity = "ra.validation.required";
  }

  if (!values.samplingIncrementStep) {
    errors.samplingIncrementStep = "ra.validation.required";
  }

  if (!values.bulkMinOrderQuantity) {
    errors.bulkMinOrderQuantity = "ra.validation.required";
  }

  if (!values.bulkMaxOrderQuantity) {
    errors.bulkMaxOrderQuantity = "ra.validation.required";
  }

  if (!values.bulkIncrementStep) {
    errors.bulkIncrementStep = "ra.validation.required";
  }

  // 印製方式驗證
  if (!Array.isArray(values.printMethods) || values.printMethods.length === 0) {
    errors.printMethods = "ra.validation.required";
  } else {
    const ids = values.printMethods
      .map(m => m?.printingMethodId)
      .filter(id => id != null);

    const hasValidMethod = ids.length > 0;
    if (!hasValidMethod) {
      errors.printMethods = "ra.validation.required";
    } else {
      // 檢查是否有重複
      const uniqueIds = new Set(ids);
      if (uniqueIds.size !== ids.length) {
        errors.printMethods = "已選的印製方式有重複";
      }
    }
  }

  // 顏色驗證
  if (!Array.isArray(values.colors) || values.colors.length === 0) {
    errors.colors = "ra.validation.required";
  } else {
    const hasValidColor = values.colors.some(color => !!color.name && !!color.code);
    const len = values.colors.length;
    const rowErrors = Array(len);

    const normHex  = (v) => (v ?? "").toString().replace(/^#/, "").toUpperCase();
    const normName = (v) => (v ?? "").toString().trim().toLowerCase();

    const codeMap = new Map(); // hex
    const nameMap = new Map(); // name

    values.colors.forEach((row, idx) => {
      const code = normHex(row?.code);
      const name = normName(row?.name);

      if (code) {
        const arr = codeMap.get(code) || [];
        arr.push(idx);
        codeMap.set(code, arr);
      }
      if (name) {
        const arr = nameMap.get(name) || [];
        arr.push(idx);
        nameMap.set(name, arr);
      }
    });

    codeMap.forEach((idxs) => {
      if (idxs.length > 1) {
        idxs.forEach((i) => {
          rowErrors[i] = { ...(rowErrors[i] || {}), code: "顏色代碼重複" };
        });
      }
    });
    nameMap.forEach((idxs) => {
      if (idxs.length > 1) {
        idxs.forEach((i) => {
          rowErrors[i] = { ...(rowErrors[i] || {}), name: "顏色名稱重複" };
        });
      }
    });

    // 只有當至少一列有錯誤時，才把陣列掛回去
    if (rowErrors.some(Boolean)) {
      errors.colors = rowErrors;
    }

    if (!hasValidColor) {
      errors.colors = "ra.validation.required";
    }
  }

  // 數量折扣驗證
  if (Array.isArray(values.quantityDiscounts)) {
    const discountsErrors = [];
    values.quantityDiscounts.forEach((item, index) => {
      if (item.minQuantity && item.maxQuantity) {
        const itemErrors = {};
        if (!item.discountPrice) {
          itemErrors.discountPrice = "ra.validation.required";
        }
        if (!item.leadTimeDays) {
          itemErrors.leadTimeDays = "ra.validation.required";
        }
        if (Object.keys(itemErrors).length > 0) {
          discountsErrors[index] = itemErrors;
        }
      }
    });
    if (discountsErrors.length > 0) {
      errors.quantityDiscounts = discountsErrors;
    }
  }

  // 編輯區域驗證
  if (!Array.isArray(values.editableAreas) || values.editableAreas.length === 0) {
    errors.editableAreas = "ra.validation.required";
  } else {
    const editableErrors = [];
    let hasValid = false;

    values.editableAreas.forEach((area, i) => {
      const err = {};

      if (!area.areaName) return;

      if (!area.templateImage?.rawFile && !area.templateImage?.[0]?.title) {
        err.templateImage = "ra.validation.required";
      }

      if (Array.isArray(area.images)) {
        const imageErrors = [];
        let hasImageError = false;

        area.images.forEach((img, j) => {
          if (!img.image?.rawFile && !img.image?.[0]?.title) {
            imageErrors[j] = { image: "ra.validation.required" };
            hasImageError = true;
          }
        });

        if (hasImageError) {
          err.images = imageErrors;
        }
      }

      if (Object.keys(err).length > 0) {
        editableErrors[i] = err;
      } else {
        hasValid = true;
      }
    });

    if (!hasValid) {
      errors.editableAreas = "ra.validation.required";
    } else if (editableErrors.length > 0) {
      errors.editableAreas = editableErrors;
    }
  }

  const validateSpecWeights = (base) => {
    const sizes = values?.[base]?.sizes;
    // 若陣列不存在或為空，視為未勾選任何規格
    if (!Array.isArray(sizes) || sizes.length === 0) {
      errors[base] = { sizes: [{ enabled: "至少要勾選一個規格" }] };
      return;
    }

    const rowsErrors = [];
    const enabledCount = sizes.filter((row) => !!row?.enabled).length;

    // 至少要勾選一個
    if (enabledCount === 0) {
      rowsErrors[0] = { ...(rowsErrors[0] || {}), enabled: "至少要勾選一個規格" };
    }

    // 打開的列 weight 必填
    sizes.forEach((row, idx) => {
      if (row?.enabled) {
        const empty = row?.weight === undefined || row?.weight === null || row?.weight === "";
        if (empty) {
          rowsErrors[idx] = { ...(rowsErrors[idx] || {}), weight: "ra.validation.required" };
        }
      }
    });

    if (rowsErrors.length > 0) {
      errors[base] = { ...(errors[base] || {}), sizes: rowsErrors };
    }
  };

  ["blankSampleSpecifications", "samplingSpecifications", "bulkSpecifications"].forEach(validateSpecWeights);

  return errors;
};

const ClothesForm = () => {
  const record = useRecordContext();

  const ProductToolbar = () => {
    const record = useRecordContext();

    return (
      <Toolbar>
        <Box display="flex" justifyContent="space-between" width="100%">
          <SaveButton />
          {record && <DeleteWithConfirmButton
              confirmTitle="確認刪除"
              confirmContent="您確定要刪除此商品嗎？"
            />
          }
        </Box>
      </Toolbar>
    );
  };

  return (
    <TabbedForm
      defaultValues={defaultValues}
      validate={validateForm}
      toolbar={<ProductToolbar />}
      key={record ? `edit-${record.id}` : 'create'}
    >
      <TabbedForm.Tab
        label="圖片"
        sx={{ maxWidth: "50em" }}
      >
        <ImagesTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab
        label="基本資料"
        path="basic"
        sx={{ maxWidth: "50em" }}
      >
        <BasicInfoTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab
        label="顏色選項"
        path="color-options"
        sx={{ maxWidth: "50em" }}
      >
        <ColorOptionsTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab
        label="數量折扣規則"
        path="quantity-discounts"
        sx={{ maxWidth: "50em" }}
      >
        <QuantityDiscountsTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab
        label="編輯區域"
        path="editable-areas"
        sx={{ maxWidth: "50em" }}
      >
        <EditableAreasTab />
      </TabbedForm.Tab>
      <TabbedForm.Tab
        label="描述"
        path="description"
        sx={{ maxWidth: "50em" }}
      >
        <DescriptionTab />
      </TabbedForm.Tab>
    </TabbedForm>
  )
}

export default ClothesForm